import { View, SxProp, useDripsyTheme, SafeAreaView, ScrollView, Pressable, Image } from 'dripsy';
import { ActivityIndicator, FlatList, ListRenderItemInfo, RefreshControl } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useContext, useEffect, useState } from 'react';
import { H6, TextBody, TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { PageContainerNative } from '@/features/card-account/screens/page/PageContainerNative';
import { formatAmountByDecimals } from '@/utils/numberFormatting';

import { useCardAccounts, AccountItem, CardItem } from '@/features/card-account/hooks/useCardAccounts.hook';
import { CardAccountType, CardAccountTypeColor, KycStatusColor } from '@/features/card-account/enum';
import { Icon } from '@/design-system/components/icons/Icon';
import { RootStackRoutes } from 'types';
import { CardAccountStackRoutes } from '@/features/card-account/card.navigator';
import { keyGenerator } from '@/utils/keyGenerator';
import { LayoutContext } from 'contexts/LayoutContext';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { AccountDetailsTabRoutes } from '@/features/account-management/screens/details/type';
import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { CardAccountTypeFilters, CardKycStatusFilters } from '@/features/card-account/options';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';
import { UniInput } from '@/design-system/components/inputs';

export interface AccountListProps {
  flag?: string;
}

const DEFAULT_PAGESIZE = 10;

type ColorTypes = keyof typeof CardAccountTypeColor;

export const CardAccountListNativeScreen: React.FC<AccountListProps> = (props) => {
  const { flag } = props;
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const layout = useContext(LayoutContext);
  const { theme } = useDripsyTheme();
  const { t } = useTranslation('card-account');

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGESIZE);

  const { pagination, accounts, isLoading, reload, createCardAccount } = useCardAccounts({
    current,
    pageSize,
    search
  });

  const flatlistHeight = layout.dimensions.height - 44;

  const funcCreateCardAccount = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    createCardAccount(data, onSuccess, onError);
  };

  const [enableScrollViewScroll, setEnableScrollViewScroll] = useState(true);

  return (
    <SafeAreaView sx={styles.containerSaft}>
      <ScrollView sx={styles.scrollContent} nestedScrollEnabled scrollEnabled={enableScrollViewScroll}>
        <View sx={styles.container}>
          <PageContainerNative>
            <H6 bold sx={{ mt: '$3', px: '$4', color: '$white' }}>
              {t('card-account::landing-page.title--card-account')}
            </H6>
          </PageContainerNative>
          <PageItem sx={styles.card}>
            {flag == '1' && (
              <UniButton
                wrapperSx={{ width: '100%', pt: '$5', px: '$5' }}
                labelSx={{ color: '$black' }}
                label={t('card-account-management::account-page.button--new-account')}
                onPress={() => {
                  navigation.navigate(RootStackRoutes.CardAccount, {
                    screen: CardAccountStackRoutes.CreateCardAccount,
                    params: {
                      func: funcCreateCardAccount,
                      refresh: reload
                    }
                  });
                }}
              />
            )}
            <UniButton
              wrapperSx={{ width: '100%', px: '$5' }}
              labelSx={{ color: '$black' }}
              label={t('card-account-management::account-page.button--download-transaction-report')}
              onPress={() =>
                navigationAny.navigate(RootStackRoutes.CardAccount, {
                  screen: AccountManagementStackRoutes.DownloadTransactionReport,
                  params: {
                    screen: DownloadTransactionReportStackRoutes.DownloadCardAccountTransactionReport
                  }
                })
              }
            />
            <View sx={styles.filters}>
              <UniInput
                wrapperSx={styles.filter_search}
                size="default"
                prefixIcon="search"
                placeholder={t('card-account::landing-page.text--search-by-account-name-number')}
                onValueChange={(value) => {
                  setCurrent(1);
                  setSearch(value);
                }}
              />
            </View>
            <View
              sx={{ height: flatlistHeight }}
              onStartShouldSetResponderCapture={() => {
                setEnableScrollViewScroll(false);
                return true;
              }}>
              <FlatList
                scrollEnabled={true}
                data={accounts}
                refreshControl={
                  <RefreshControl
                    refreshing={isLoading}
                    onRefresh={() => {
                      if (pageSize === DEFAULT_PAGESIZE) {
                        reload();
                      } else {
                        setPageSize(DEFAULT_PAGESIZE);
                      }
                    }}
                  />
                }
                renderItem={({ item, index }: ListRenderItemInfo<AccountItem>) => {
                  return (
                    <View>
                      {index > 0 ? <View sx={{ height: 5, backgroundColor: '$borderPrimary' }} /> : null}
                      <Pressable
                        onPress={() => {
                          if (flag != '1') {
                            window.parent.postMessage({ origin: 'card-account', data: { id: item.id } }, '*');
                          } else {
                            navigation.navigate(RootStackRoutes.AccountManagement, {
                              screen: AccountManagementStackRoutes.Details,
                              params: {
                                screen: AccountDetailsTabRoutes.Card,
                                account: item.id,
                                record: { ...item, cardAccountId: item.id },
                                tab: 1,
                                flag: flag
                              }
                            });
                          }
                        }}>
                        <View
                          sx={{
                            pt: '$5',
                            pb: '$10',
                            gap: '$5'
                          }}>
                          <View
                            sx={{ flexDirection: 'row', gap: '$2', alignItems: 'center', justifyContent: 'flex-start', paddingX: '$4' }}>
                            <Icon name="account" />
                            <TextTitle semiBold>{item.label}</TextTitle>
                          </View>
                          <View sx={{ flexDirection: 'row', gap: '$2', alignItems: 'center', paddingX: '$4', flexWrap: 'wrap' }}>
                            <View
                              sx={{
                                ...styles.tagSx,
                                backgroundColor: theme.colors[`$tag${CardAccountTypeColor[item.type as ColorTypes]}Background`] as string
                              }}>
                              <TextCaption
                                sx={{ color: theme.colors[`$tag${CardAccountTypeColor[item.type as ColorTypes]}Text`] as string }}>
                                {CardAccountTypeFilters(t).find((rec) => rec.value === item.type)?.label}
                              </TextCaption>
                            </View>
                            <View
                              sx={{
                                ...styles.tagSx,
                                backgroundColor: theme.colors?.$tagBlackBackground as string
                              }}>
                              <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{item.assetSymbol}</TextCaption>
                            </View>
                            {item.autopayEnabled && (
                              <View
                                sx={{
                                  ...styles.tagSx,
                                  backgroundColor: theme.colors?.$tagGreenBackground as string
                                }}>
                                <TextCaption sx={{ color: theme.colors?.$tagGreenText as string }}>
                                  {t('card-account::landing-page.text--utgl-autopay-enabled')}
                                </TextCaption>
                              </View>
                            )}
                            {item.cardAccountKycStatus && (
                              <View
                                sx={{
                                  ...styles.tagSx,
                                backgroundColor: theme.colors[`$tag${KycStatusColor[item.cardAccountKycStatus as ColorTypes]}Background`] as string
                                }}>
                                <TextCaption sx={{ color: theme.colors[`$tag${KycStatusColor[item.cardAccountKycStatus as ColorTypes]}Text`] as string }}>
                                  {CardKycStatusFilters(t).find((rec) => rec.value === item.cardAccountKycStatus)?.label}
                                </TextCaption>
                              </View>
                            )}
                          </View>
                          <View sx={{ gap: '$1', paddingX: '$4' }}>
                            <TextCaption semiBold sx={{ color: theme.colors?.$textSecondary as string }}>
                              {t('card-account::landing-page.text--card-account-balance')}
                            </TextCaption>
                            <H6
                              bold
                              sx={{
                                color: (item.balance > 0 && (item.type as CardAccountType) === CardAccountType.PREPAID
                                  ? theme.colors?.$tagRedBackground
                                  : theme.colors?.$textThemeSecondary) as string
                              }}>
                              {formatAmountByDecimals({ value: Math.abs(item.balance), suffix: item.assetSymbol })}{' '}
                              {item.balance < 0 && '[CR]'}
                            </H6>
                          </View>
                          {(item.type as CardAccountType) !== CardAccountType.PREPAID && (
                            <View sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', paddingX: '$4' }}>
                              <View sx={{ gap: '$1' }}>
                                <TextCaption sx={{ color: theme.colors?.$textSecondary as string }}>
                                  {t('card-account::landing-page.text--outstanding-balance')}
                                </TextCaption>
                                <TextBody semiBold>
                                  {formatAmountByDecimals({ value: Math.abs(item.outstandingBalance), suffix: item.assetSymbol })}{' '}
                                  {item.outstandingBalance < 0 && '[CR]'}
                                </TextBody>
                              </View>
                              <View sx={{ gap: '$1' }}>
                                <TextCaption sx={{ color: theme.colors?.$textSecondary as string }}>
                                  {t('card-account::landing-page.text--card-account-limit')}
                                </TextCaption>
                                <View sx={{ flexDirection: 'row', gap: '$1', alignItems: 'center' }}>
                                  <TextBody semiBold>{formatAmountByDecimals({ value: item.availableLimit })}</TextBody>
                                  <TextSmall sx={{ color: theme.colors?.$textSecondary as string }}>/</TextSmall>
                                  <TextSmall sx={{ color: theme.colors?.$textSecondary as string }}>
                                    {formatAmountByDecimals({ value: item.limit, suffix: item.assetSymbol })}
                                  </TextSmall>
                                </View>
                              </View>
                            </View>
                          )}
                          {item.cards.filter((card) => (flag == '0' && card.status != 'CANCELLED') || flag != '0').length > 0 && (
                            <ScrollView nestedScrollEnabled horizontal showsHorizontalScrollIndicator={false}>
                              <View sx={{ flexDirection: 'row', gap: '$4', padding: '$1', paddingX: '$4' }}>
                                {item.cards
                                  .filter((card) => (flag == '0' && card.status != 'CANCELLED') || flag != '0')
                                  .map((card, i) => (
                                    <Pressable
                                      sx={{
                                        width: 'max-content',
                                        boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.1)',
                                        flexDirection: 'row',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        padding: '$4',
                                        gap: '$4',
                                        borderRadius: 10,
                                        backgroundColor: ['PENDING_ACTIVATION'].includes(card?.status) ? '#FDFAED' : undefined,
                                        border: ['PENDING_ACTIVATION'].includes(card?.status) ? '1px solid #DC9B32' : undefined
                                      }}
                                      key={`card-item-${i}`}
                                      onPress={() => {
                                        if (flag != '1') {
                                          window.parent.postMessage({ origin: 'card-account', data: { id: item.id, card: card.id } }, '*');
                                        } else {
                                          navigation.navigate(RootStackRoutes.AccountManagement, {
                                            screen: AccountManagementStackRoutes.Details,
                                            params: {
                                              screen: AccountDetailsTabRoutes.Card,
                                              account: item.id,
                                              record: { ...item, cardAccountId: item.id },
                                              tab: 1,
                                              flag: flag,
                                              options: { card }
                                            }
                                          });
                                        }
                                      }}>
                                      <Image
                                        sx={styles.imageSx}
                                        source={item.cards[0].productImageUrl || require('assets/images/worldcard_blank.png')}
                                      />
                                      <View sx={{ flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: '$1' }}>
                                        <View
                                          sx={{
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '$2',
                                            width: '100%'
                                          }}>
                                          <TextTitle semiBold>
                                            {card.embossedName}
                                            {['PENDING_ACTIVATION'].includes(card?.status) && ` ${card.last4}`}
                                          </TextTitle>
                                          {['LOCKED', 'SUSPENDED'].includes(card?.status) ? (
                                            <Icon name={card?.status == 'LOCKED' ? 'lock1' : 'cardSuspended'} />
                                          ) : (
                                            <View sx={{ width: '$5' }} />
                                          )}
                                        </View>
                                        {['PENDING_ACTIVATION'].includes(card?.status) ? (
                                          <View
                                            sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', height: '$5' }}>
                                            <TextCaption sx={{ color: theme.colors?.$textHyperlink as string }}>
                                              {t('card::main-card.view-all-cards.button--activate-card')}
                                            </TextCaption>
                                            <Icon name="arrowRightOrange" color={theme.colors?.$textHyperlink as string} />
                                          </View>
                                        ) : (
                                          <TextCaption sx={{ color: theme.colors?.$textSecondary as string }}>
                                            {card.productName} {`**** ${card.last4}`}
                                          </TextCaption>
                                        )}
                                      </View>
                                    </Pressable>
                                  ))}
                              </View>
                            </ScrollView>
                          )}
                        </View>
                      </Pressable>
                    </View>
                  );
                }}
                showsVerticalScrollIndicator={false}
                keyExtractor={(_, index) => keyGenerator(`card-account-list-${index}`)}
                ListFooterComponent={() => {
                  return isLoading ? (
                    <View sx={{ padding: '$5' }}>
                      <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} animating={true} />
                    </View>
                  ) : null;
                }}
                onEndReachedThreshold={0.2}
                onEndReached={() => {
                  if (pagination.total > accounts.length && !isLoading) {
                    setPageSize((prev) => prev + DEFAULT_PAGESIZE);
                  }
                }}
              />
            </View>
          </PageItem>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  containerSaft = 'containerSaft',
  scrollContent = 'scrollContent',
  container = 'container',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search',
  imageSx = 'imageSx',
  tagSx = 'tagSx',
  productFilterNativeIconBtn = 'productFilterNativeIconBtn'
}

const styles: Record<StyleName, SxProp> = {
  containerSaft: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary'
  },
  scrollContent: {
    flex: 1,
    height: '100%',
    backgroundColor: '$backgroundSecondary'
  },
  container: {
    // margin: '$4',
    flexDirection: 'column',
    height: '100%'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$4',
    mt: '$4',
    paddingY: '$0'
  },
  table: {
    flexDirection: 'column',
    gap: '$4'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    pl: '$2',
    pr: '$2'
  },
  filter_search: {
    width: '100%'
  },
  imageSx: { width: 49, height: 31 },
  tagSx: { paddingX: '$2', paddingY: '$1', width: 'fit-content', borderRadius: 100 },
  productFilterNativeIconBtn: {
    height: '$6',
    width: '$6',
    justifyContent: 'center',
    alignItems: 'center'
  }
};
